import React, { useState } from "react";
import { useMarkedItems } from "../lib/markedItems";

/**
 * Per-row affordance for marking an item or a dimension pair.
 *
 * Two states:
 *   - unmarked → "+ Mark" button. Click opens a tiny popover with a note
 *     field and a confirm button. Submit persists the mark with the note.
 *   - marked  → "✓ Marked" button. Click opens the same popover pre-filled
 *     with the existing note so it can be edited or the mark removed.
 *
 * Looks small and quiet by design — the surfaces (Divergence, Compare,
 * Drill) are about reading the data; the mark is a side gesture.
 */
export default function MarkButton({ kind, payload, label = "Mark", testIdSuffix }) {
  const { isMarked, create, updateNote, remove, open: openTray } = useMarkedItems();
  const existing = isMarked(kind, payload);
  const [showPopover, setShowPopover] = useState(false);
  const [note, setNote] = useState(existing?.consultant_note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function openPopover() {
    setNote(existing?.consultant_note || "");
    setError("");
    setShowPopover(true);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      if (existing) {
        await updateNote(existing.id, note.trim());
      } else {
        await create(kind, payload, note.trim());
      }
      setShowPopover(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!existing) return;
    setSaving(true);
    setError("");
    try {
      await remove(existing.id);
      setShowPopover(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  }

  const testId = `mark-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`;
  const isExisting = Boolean(existing);

  return (
    <div className="relative inline-block">
      <button
        onClick={openPopover}
        data-testid={testId}
        data-marked={isExisting ? "true" : "false"}
        title={isExisting ? "Edit note · view in tray · remove" : "Mark this for the report"}
        style={{
          fontFamily: "Oswald",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontSize: "10px",
          padding: "5px 10px",
          background: isExisting ? "#1A1A1A" : "transparent",
          color: isExisting ? "#FBFAF7" : "#1A1A1A",
          border: `1px solid ${isExisting ? "#1A1A1A" : "#DDDDDD"}`,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {isExisting ? "✓ Marked" : `+ ${label}`}
      </button>
      {showPopover && (
        <div
          className="absolute z-50 mt-2"
          style={{
            right: 0,
            width: "320px",
            background: "#FBFAF7",
            border: "1px solid #DDDDDD",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            padding: "14px",
          }}
          data-testid={`${testId}-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="eyebrow mb-2" style={{ fontSize: "10px" }}>
            {isExisting ? "Edit note" : "Add to report"}
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A sentence in your own words about why this matters."
            rows={3}
            maxLength={500}
            className="field w-full text-sm"
            style={{ fontFamily: "Inter", lineHeight: 1.5 }}
            data-testid={`${testId}-note`}
          />
          <div className="text-xs text-gray-500 mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.08em" }}>
            {note.length} / 500
          </div>
          {error && (
            <div className="text-xs mt-2" style={{ color: "#AD393E" }}>{error}</div>
          )}
          <div className="flex items-center justify-between gap-2 mt-3">
            <button
              onClick={() => setShowPopover(false)}
              className="text-xs"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                background: "transparent", border: 0, color: "#555", cursor: "pointer", padding: "6px 0",
              }}
              data-testid={`${testId}-cancel`}
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {isExisting && (
                <button
                  onClick={handleRemove}
                  disabled={saving}
                  className="text-xs"
                  style={{
                    fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                    background: "transparent", border: "1px solid #AD393E", color: "#AD393E",
                    padding: "6px 10px", cursor: saving ? "wait" : "pointer",
                  }}
                  data-testid={`${testId}-remove`}
                >
                  Remove
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-xs"
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                  background: "#1A1A1A", color: "#FBFAF7", border: 0,
                  padding: "6px 12px", cursor: saving ? "wait" : "pointer",
                }}
                data-testid={`${testId}-save`}
              >
                {isExisting ? "Save" : "Mark"}
              </button>
            </div>
          </div>
          {isExisting && (
            <button
              onClick={() => { setShowPopover(false); openTray(); }}
              className="text-xs mt-2"
              style={{
                fontFamily: "Oswald", letterSpacing: "0.12em", textTransform: "uppercase",
                background: "transparent", border: 0, color: "#2F6B7C", cursor: "pointer", padding: "4px 0",
              }}
              data-testid={`${testId}-view-tray`}
            >
              View all marked →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
